import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Package, Mail, Lock, User, Phone, Building, Loader2, 
  Truck, ChevronRight, ChevronLeft, CheckCircle2, AlertTriangle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { toast } from '../hooks/use-toast';
import { countries } from '../mockData';
import CGVModal from '../components/CGVModal';
import axios from 'axios';
import { logger } from '../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showCGVModal, setShowCGVModal] = useState(false);
  
  // Configuration des inscriptions
  const [registrationConfig, setRegistrationConfig] = useState({
    client_registration_enabled: true,
    driver_registration_enabled: true,
    registration_closed_message: ''
  });
  const [loadingConfig, setLoadingConfig] = useState(true);
  
  // Branding (configurable depuis les paramètres)
  const [branding, setBranding] = useState({
    company_name: 'Baobab Connect',
    logo_url: null,
    login_background_url: null,
    login_background_type: 'gradient',
    primary_color: '#1db584'
  });
  
  // Étape de l'inscription
  const [profileType, setProfileType] = useState(null); // 'client' ou 'driver'
  
  // Liste des secteurs d&apos;activité
  const businessSectors = [
    { value: 'agriculture', label: 'Agriculture' },
    { value: 'agroalimentaire', label: 'Agroalimentaire' },
    { value: 'btp_construction', label: 'BTP / Construction' },
    { value: 'commerce_distribution', label: 'Commerce / Distribution' },
    { value: 'energie_mines', label: 'Énergie / Mines' },
    { value: 'industrie_manufacture', label: 'Industrie / Manufacture' },
    { value: 'logistique_transport', label: 'Logistique / Transport' },
    { value: 'sante_pharmaceutique', label: 'Santé / Pharmaceutique' },
    { value: 'services_conseil', label: 'Services / Conseil' },
    { value: 'technologies_telecoms', label: 'Technologies / Télécoms' },
    { value: 'textile_mode', label: 'Textile / Mode' },
    { value: 'autre', label: 'Autre' }
  ];
  
  // Données client (existant)
  const [clientData, setClientData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    country: 'ci',
    password: '',
    confirmPassword: '',
    accountType: 'particulier',
    companyName: '',
    sector: '',
    sectorOther: '',
    acceptedCGV: false
  });
  
  // Données chauffeur - SIMPLIFIÉES
  const [driverData, setDriverData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    country: 'ci',
    password: '',
    confirmPassword: '',
    accountType: 'individual', // individual ou company,
    companyName: '',
    acceptedCGV: false
  });

  // Charger les paramètres de branding et inscriptions (endpoints publics)
  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/system-config/branding/public`);
        if (response.ok) {
          const data = await response.json();
          setBranding(prev => ({
            ...prev,
            ...data
          }));
        }
      } catch (error) {
        logger.debug('Using default branding:', error);
      }
    };
    
    const fetchRegistrationConfig = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/system-config/registration/public`);
        if (response.ok) {
          const data = await response.json();
          setRegistrationConfig(data);
        }
      } catch (error) {
        logger.debug('Using default registration config:', error);
      } finally {
        setLoadingConfig(false);
      }
    };
    
    fetchBranding();
    fetchRegistrationConfig();
  }, []);

  // Redirection automatique si les inscriptions sont fermées
  useEffect(() => {
    if (!loadingConfig) {
      const noRegistrationAvailable = !registrationConfig.client_registration_enabled && !registrationConfig.driver_registration_enabled;
      if (noRegistrationAvailable) {
        navigate('/login');
      }
    }
  }, [loadingConfig, registrationConfig, navigate]);

  // Construire l'URL complète pour les images
  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url}`;
  };

  // Déterminer le style de fond
  const getBackgroundStyle = () => {
    if (branding.login_background_type === 'image' && branding.login_background_url) {
      return {
        backgroundImage: `url(${getImageUrl(branding.login_background_url)})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      };
    }
    // Dégradé par défaut avec la couleur primaire
    const color = branding.primary_color || '#1db584';
    return {
      background: `linear-gradient(to bottom right, ${color}, ${adjustColor(color, -20)}, ${adjustColor(color, -40)})`
    };
  };

  // Fonction pour ajuster la luminosité d'une couleur
  const adjustColor = (color, amount) => {
    const hex = color.replace('#', '');
    const num = parseInt(hex, 16);
    const r = Math.min(255, Math.max(0, (num >> 16) + amount));
    const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00FF) + amount));
    const b = Math.min(255, Math.max(0, (num & 0x0000FF) + amount));
    return `#${(1 << 24 | r << 16 | g << 8 | b).toString(16).slice(1)}`;
  };

  const handleClientChange = (e) => {
    setClientData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };
  
  const handleDriverChange = (e) => {
    setDriverData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  // Soumission client (existant)
  const handleClientSubmit = async (e) => {
    e.preventDefault();

    if (!clientData.acceptedCGV) {
      toast({
        title: "Erreur",
        description: "Vous devez accepter les Conditions Générales de Vente pour continuer",
        variant: "destructive"
      });
      return;
    }

    if (clientData.password !== clientData.confirmPassword) {
      toast({
        title: "Erreur",
        description: "Les mots de passe ne correspondent pas",
        variant: "destructive"
      });
      return;
    }

    if (clientData.password.length < 6) {
      toast({
        title: "Erreur",
        description: "Le mot de passe doit contenir au moins 6 caractères",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);

    const result = await register({
      firstName: clientData.firstName,
      lastName: clientData.lastName,
      email: clientData.email,
      phone: clientData.phone,
      country: clientData.country,
      password: clientData.password,
      accountType: clientData.accountType,
      companyName: clientData.accountType === 'entreprise' ? clientData.companyName : undefined,
      sector: clientData.accountType === 'entreprise' ? clientData.sector : undefined,
      sectorOther: clientData.accountType === 'entreprise' && clientData.sector === 'autre' ? clientData.sectorOther : undefined
    });
    
    if (result.success) {
      if (result.requiresVerification) {
        // New flow: redirect to verification pending page
        toast({
          title: "✅ Inscription réussie",
          description: "Vérifiez votre email pour activer votre compte."
        });
        // Redirect to login with a message
        navigate('/login?registered=true&email=' + encodeURIComponent(clientData.email));
      } else {
        // Legacy flow: direct login
        toast({
          title: "✅ Inscription réussie",
          description: "Bienvenue sur Baobab Connect"
        });
        navigate('/dashboard');
      }
    } else {
      toast({
        title: result.errorTitle || "Erreur d&apos;inscription",
        description: result.error,
        variant: "destructive"
      });
    }
    
    setIsLoading(false);
  };
  
  // Soumission chauffeur - SIMPLIFIÉE
  const handleDriverSubmit = async (e) => {
    e.preventDefault();

    if (!driverData.acceptedCGV) {
      toast({
        title: "Erreur",
        description: "Vous devez accepter les Conditions Générales",
        variant: "destructive"
      });
      return;
    }

    if (driverData.password !== driverData.confirmPassword) {
      toast({
        title: "Erreur",
        description: "Les mots de passe ne correspondent pas",
        variant: "destructive"
      });
      return;
    }

    if (driverData.password.length < 6) {
      toast({
        title: "Erreur",
        description: "Le mot de passe doit contenir au moins 6 caractères",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/driver/auth/register-simple`, {
        firstName: driverData.firstName,
        lastName: driverData.lastName,
        email: driverData.email,
        phone: driverData.phone,
        password: driverData.password,
        accountType: driverData.accountType,
        companyName: driverData.accountType === 'company' ? driverData.companyName : undefined
      });
      
      toast({
        title: "✅ Inscription réussie",
        description: response.data.message || "Votre compte est en attente de validation"
      });
      
      navigate('/login?pending=driver');
      
    } catch (error) {
      const errorCode = error.response?.data?.detail || '';
      
      // Map error codes to user-friendly messages
      let errorTitle = "Erreur d&apos;inscription";
      let errorMessage = "Une erreur s'est produite lors de l'inscription";
      
      if (errorCode === 'EMAIL_ALREADY_EXISTS') {
        errorTitle = "Email déjà utilisé";
        errorMessage = "Cette adresse email est déjà associée à un compte. Veuillez utiliser une autre adresse ou vous connecter.";
      } else if (errorCode === 'PHONE_ALREADY_EXISTS') {
        errorTitle = "Numéro déjà utilisé";
        errorMessage = "Ce numéro de téléphone est déjà associé à un compte chauffeur. Veuillez utiliser un autre numéro.";
      } else if (errorCode) {
        errorMessage = errorCode;
      }
      
      toast({
        title: errorTitle,
        description: errorMessage,
        variant: "destructive"
      });
    }
    
    setIsLoading(false);
  };

  // Écran de sélection du profil
  const renderProfileSelection = () => {
    const noRegistrationAvailable = !registrationConfig.client_registration_enabled && !registrationConfig.driver_registration_enabled;
    
    // Si aucune inscription n'est disponible, afficher un loader (la redirection se fera via useEffect)
    if (noRegistrationAvailable) {
      return (
        <div className="flex items-center justify-center p-8">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      );
    }
    
    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h2 className="text-xl font-semibold text-gray-800">Comment souhaitez-vous utiliser Baobab Connect ?</h2>
          <p className="text-gray-500 mt-2">Choisissez votre profil pour continuer</p>
        </div>
        
        <div className="grid gap-4">
          {/* Option Expéditeur - affiché seulement si activé */}
          {registrationConfig.client_registration_enabled && (
            <button
              onClick={() => setProfileType('client')}
              className="p-6 border-2 border-gray-200 rounded-xl hover:border-[#1db584] hover:bg-[#1db584]/5 transition-all text-left group"
            >
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-xl bg-[#1db584]/10 flex items-center justify-center group-hover:bg-[#1db584]/20 transition-colors">
                  <Package className="w-7 h-7 text-[#1db584]" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg text-gray-900">Je suis Expéditeur</h3>
                  <p className="text-gray-500 text-sm mt-1">
                    Je souhaite envoyer des marchandises et suivre mes opérations de transport
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-[#1db584] mt-4" />
              </div>
            </button>
          )}
          
          {/* Option Chauffeur - affiché seulement si activé */}
          {registrationConfig.driver_registration_enabled && (
            <button
              onClick={() => setProfileType('driver')}
              className="p-6 border-2 border-gray-200 rounded-xl hover:border-[#1a1a2e] hover:bg-[#1a1a2e]/5 transition-all text-left group"
            >
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-xl bg-[#1a1a2e]/10 flex items-center justify-center group-hover:bg-[#1a1a2e]/20 transition-colors">
                  <Truck className="w-7 h-7 text-[#1a1a2e]" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg text-gray-900">Je suis Chauffeur / Transporteur</h3>
                  <p className="text-gray-500 text-sm mt-1">
                    Je souhaite recevoir et gérer des missions de transport
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-[#1a1a2e] mt-4" />
              </div>
            </button>
          )}
          
          {/* Message si un type est désactivé */}
          {(!registrationConfig.client_registration_enabled || !registrationConfig.driver_registration_enabled) && (
            <div className="text-center text-sm text-gray-500 bg-gray-50 p-3 rounded-lg">
              {!registrationConfig.client_registration_enabled && registrationConfig.driver_registration_enabled && (
                <span>Les inscriptions expéditeurs sont fermées.</span>
              )}
              {registrationConfig.client_registration_enabled && !registrationConfig.driver_registration_enabled && (
                <span>Les inscriptions chauffeurs sont fermées.</span>
              )}
            </div>
          )}
        </div>
        
        <p className="text-center text-sm text-gray-600 mt-6">
          Déjà un compte ?{' '}
          <Link to="/login" className="text-[#1db584] hover:text-[#16a075] font-semibold hover:underline">
            Se connecter
          </Link>
        </p>
      </div>
    );
  };

  // Formulaire client (existant)
  const renderClientForm = () => (
    <form onSubmit={handleClientSubmit} className="space-y-4">
      <button
        type="button"
        onClick={() => setProfileType(null)}
        className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-4"
      >
        <ChevronLeft className="w-4 h-4" />
        Retour au choix du profil
      </button>
      
      {/* Account Type */}
      <div className="space-y-2">
        <Label>Type de compte</Label>
        <RadioGroup 
          value={clientData.accountType} 
          onValueChange={(value) => setClientData(prev => ({ ...prev, accountType: value }))}
          className="flex gap-4"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="particulier" id="particulier" className="border-[#1db584] text-[#1db584]" />
            <Label htmlFor="particulier" className="cursor-pointer">Particulier</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="entreprise" id="entreprise" className="border-[#1db584] text-[#1db584]" />
            <Label htmlFor="entreprise" className="cursor-pointer">Entreprise</Label>
          </div>
        </RadioGroup>
      </div>

      {/* Company Name */}
      {clientData.accountType === 'entreprise' && (
        <div className="space-y-2">
          <Label htmlFor="companyName">Nom de l'entreprise *</Label>
          <div className="relative">
            <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              id="companyName"
              name="companyName"
              type="text"
              placeholder="Nom de votre entreprise"
              value={clientData.companyName}
              onChange={handleClientChange}
              className="pl-10"
              required={clientData.accountType === 'entreprise'}
            />
          </div>
        </div>
      )}

      {/* Sector (for entreprise only) */}
      {clientData.accountType === 'entreprise' && (
        <div className="space-y-2">
          <Label htmlFor="sector">Secteur d&apos;activité *</Label>
          <Select
            value={clientData.sector}
            onValueChange={(value) => setClientData(prev => ({ ...prev, sector: value, sectorOther: value !== 'autre' ? '' : prev.sectorOther }))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Sélectionnez votre secteur" />
            </SelectTrigger>
            <SelectContent>
              {businessSectors.map(sector => (
                <SelectItem key={sector.value} value={sector.value}>
                  {sector.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Custom sector if "autre" is selected */}
      {clientData.accountType === 'entreprise' && clientData.sector === 'autre' && (
        <div className="space-y-2">
          <Label htmlFor="sectorOther">Précisez votre secteur *</Label>
          <Input
            id="sectorOther"
            name="sectorOther"
            type="text"
            placeholder="Ex: Consulting, Import-Export..."
            value={clientData.sectorOther}
            onChange={handleClientChange}
            required
          />
        </div>
      )}

      {/* Name Fields */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="firstName">Prénom *</Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              id="firstName"
              name="firstName"
              type="text"
              placeholder="Prénom"
              value={clientData.firstName}
              onChange={handleClientChange}
              className="pl-10"
              required
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="lastName">Nom *</Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              id="lastName"
              name="lastName"
              type="text"
              placeholder="Nom"
              value={clientData.lastName}
              onChange={handleClientChange}
              className="pl-10"
              required
            />
          </div>
        </div>
      </div>

      {/* Email */}
      <div className="space-y-2">
        <Label htmlFor="email">Email *</Label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            id="email"
            name="email"
            type="email"
            placeholder="votre@email.com"
            value={clientData.email}
            onChange={handleClientChange}
            className="pl-10"
            required
          />
        </div>
      </div>

      {/* Phone */}
      <div className="space-y-2">
        <Label htmlFor="phone">Téléphone *</Label>
        <div className="flex gap-2">
          <Select value={clientData.country} onValueChange={(value) => setClientData(prev => ({ ...prev, country: value }))}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {countries.map((country) => (
                <SelectItem key={country.value} value={country.value}>
                  {country.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="relative flex-1">
            <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              id="phone"
              name="phone"
              type="tel"
              placeholder="Numéro de téléphone"
              value={clientData.phone}
              onChange={handleClientChange}
              className="pl-10"
              required
            />
          </div>
        </div>
      </div>

      {/* Password Fields */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="password">Mot de passe *</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              id="password"
              name="password"
              type="password"
              placeholder="••••••••"
              value={clientData.password}
              onChange={handleClientChange}
              className="pl-10"
              required
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirmPassword">Confirmer *</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              placeholder="••••••••"
              value={clientData.confirmPassword}
              onChange={handleClientChange}
              className="pl-10"
              required
            />
          </div>
        </div>
      </div>

      {/* CGV */}
      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
        <div className="flex items-start space-x-3">
          <input
            type="checkbox"
            id="acceptCGV"
            checked={clientData.acceptedCGV}
            onChange={(e) => setClientData(prev => ({ ...prev, acceptedCGV: e.target.checked }))}
            className="mt-1 h-4 w-4 text-[#1db584] border-gray-300 rounded focus:ring-[#1db584]"
            required
          />
          <label htmlFor="acceptCGV" className="text-sm text-gray-700 cursor-pointer">
            J'accepte les{' '}
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); setShowCGVModal(true); }}
              className="text-[#1db584] font-medium hover:underline"
            >
              Conditions Générales de Vente
            </button>
            {' '}de Baobab Connect *
          </label>
        </div>
      </div>

      <Button
        type="submit"
        className="w-full bg-gradient-to-r from-[#1db584] to-[#16a075] hover:from-[#16a075] hover:to-[#128f69] text-white"
        disabled={isLoading || !clientData.acceptedCGV}
      >
        {isLoading ? (
          <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Création du compte...</>
        ) : (
          'Créer mon compte'
        )}
      </Button>
    </form>
  );

  // Formulaire chauffeur - SIMPLIFIÉ (une seule page)
  const renderDriverForm = () => (
    <form onSubmit={handleDriverSubmit} className="space-y-4">
      <button
        type="button"
        onClick={() => setProfileType(null)}
        className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-4"
      >
        <ChevronLeft className="w-4 h-4" />
        Retour au choix du profil
      </button>
      
      {/* Type de compte chauffeur */}
      <div className="space-y-2">
        <Label>Type de compte</Label>
        <RadioGroup 
          value={driverData.accountType} 
          onValueChange={(value) => setDriverData(prev => ({ ...prev, accountType: value }))}
          className="flex gap-4"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="individual" id="individual" className="border-[#1a1a2e] text-[#1a1a2e]" />
            <Label htmlFor="individual" className="cursor-pointer">Chauffeur indépendant</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="company" id="company" className="border-[#1a1a2e] text-[#1a1a2e]" />
            <Label htmlFor="company" className="cursor-pointer">Société de transport</Label>
          </div>
        </RadioGroup>
      </div>
      
      {/* Nom société */}
      {driverData.accountType === 'company' && (
        <div className="space-y-2">
          <Label htmlFor="companyName">Nom de la société *</Label>
          <div className="relative">
            <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              id="companyName"
              name="companyName"
              placeholder="Nom de votre société"
              value={driverData.companyName}
              onChange={handleDriverChange}
              className="pl-10"
              required
            />
          </div>
        </div>
      )}

      {/* Nom Prénom */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="firstName">Prénom *</Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              name="firstName"
              placeholder="Prénom"
              value={driverData.firstName}
              onChange={handleDriverChange}
              className="pl-10"
              required
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="lastName">Nom *</Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              name="lastName"
              placeholder="Nom"
              value={driverData.lastName}
              onChange={handleDriverChange}
              className="pl-10"
              required
            />
          </div>
        </div>
      </div>

      {/* Email */}
      <div className="space-y-2">
        <Label>Email *</Label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            name="email"
            type="email"
            placeholder="votre@email.com"
            value={driverData.email}
            onChange={handleDriverChange}
            className="pl-10"
            required
          />
        </div>
      </div>

      {/* Phone */}
      <div className="space-y-2">
        <Label>Téléphone *</Label>
        <div className="flex gap-2">
          <Select value={driverData.country} onValueChange={(value) => setDriverData(prev => ({ ...prev, country: value }))}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {countries.map((country) => (
                <SelectItem key={country.value} value={country.value}>
                  {country.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="relative flex-1">
            <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              name="phone"
              type="tel"
              placeholder="Numéro de téléphone"
              value={driverData.phone}
              onChange={handleDriverChange}
              className="pl-10"
              required
            />
          </div>
        </div>
      </div>

      {/* Password */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Mot de passe *</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              name="password"
              type="password"
              placeholder="••••••••"
              value={driverData.password}
              onChange={handleDriverChange}
              className="pl-10"
              required
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Confirmer *</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              name="confirmPassword"
              type="password"
              placeholder="••••••••"
              value={driverData.confirmPassword}
              onChange={handleDriverChange}
              className="pl-10"
              required
            />
          </div>
        </div>
      </div>

      {/* Info complémentaire */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-700">
          <strong>Note :</strong> Vous pourrez compléter votre profil (permis, CNI, véhicules) après l'inscription depuis votre espace personnel.
        </p>
      </div>

      {/* CGV */}
      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
        <div className="flex items-start space-x-3">
          <input
            type="checkbox"
            id="driverCGV"
            checked={driverData.acceptedCGV}
            onChange={(e) => setDriverData(prev => ({ ...prev, acceptedCGV: e.target.checked }))}
            className="mt-1 h-4 w-4 text-[#1a1a2e] border-gray-300 rounded"
            required
          />
          <label htmlFor="driverCGV" className="text-sm text-gray-700 cursor-pointer">
            J'accepte les{' '}
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); setShowCGVModal(true); }}
              className="text-[#1a1a2e] font-medium hover:underline"
            >
              Conditions Générales d'Utilisation
            </button>
            {' '}de Baobab Connect *
          </label>
        </div>
      </div>

      <Button
        type="submit"
        className="w-full bg-[#1a1a2e] hover:bg-[#16213e] text-white"
        disabled={isLoading || !driverData.acceptedCGV}
      >
        {isLoading ? (
          <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Création du compte...</>
        ) : (
          <><CheckCircle2 className="mr-2 w-4 h-4" /> Créer mon compte chauffeur</>
        )}
      </Button>
    </form>
  );

  // Rendu conditionnel
  const renderContent = () => {
    if (loadingConfig) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      );
    }
    if (!profileType) return renderProfileSelection();
    if (profileType === 'client') return renderClientForm();
    if (profileType === 'driver') return renderDriverForm();
    return renderProfileSelection();
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={getBackgroundStyle()}
    >
      {/* Overlay sombre si image de fond */}
      {branding.login_background_type === 'image' && branding.login_background_url && (
        <div className="absolute inset-0 bg-black/40 z-0" />
      )}
      
      <Card className="w-full max-w-lg shadow-2xl relative z-10">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center mb-4">
            {branding.logo_url ? (
              <img 
                src={getImageUrl(branding.logo_url)} 
                alt={branding.company_name} 
                className="h-16 object-contain"
              />
            ) : (
              <div 
                className="p-3 rounded-xl shadow-md"
                style={{ background: `linear-gradient(to bottom right, ${branding.primary_color}, ${adjustColor(branding.primary_color, -20)})` }}
              >
                <Package className="w-8 h-8 text-white" />
              </div>
            )}
          </div>
          <CardTitle className="text-2xl font-bold">
            {!profileType ? `Rejoindre ${branding.company_name}` : 
             profileType === 'client' ? 'Inscription Expéditeur' : 'Inscription Chauffeur'}
          </CardTitle>
          <CardDescription>
            {!profileType ? 'La plateforme de transport et logistique en Afrique' :
             profileType === 'client' ? 'Créez votre compte pour expédier vos marchandises' : 
             'Créez votre compte pour recevoir des missions'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {renderContent()}
        </CardContent>
      </Card>

      <CGVModal
        isOpen={showCGVModal}
        onClose={() => setShowCGVModal(false)}
        onAccept={() => {
          if (profileType === 'client') {
            setClientData(prev => ({ ...prev, acceptedCGV: true }));
          } else {
            setDriverData(prev => ({ ...prev, acceptedCGV: true }));
          }
          setShowCGVModal(false);
        }}
      />
    </div>
  );
};

export default Register;
